'use client';

import { Link, List, ListItem } from '@coolblue-development/freddy';
import selectEnergyLabelInformationTrigger from '@coolblue-development/ga4-events/triggers/Configurator/selectEnergyLabelInformationTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import EnergyLabel from '@coolblue-development/next-energy-label/client';
import { MiniProductPage } from '@coolblue-development/next-mini-product-page/client';
import {
  type AlternativeProductCardProps,
  ProductCard,
  ProductTitle,
  UsbCChargerIncluded
} from '@coolblue-development/next-patterns/client';
import { PromotionalProductImage } from '@coolblue-development/next-patterns/core';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import {
  SalesPrice,
  SalesPriceCurrent,
  SalesPriceFormer
} from '@coolblue-development/next-sales-price/client';
import styled from '@emotion/styled';
import { PersonalPrice } from '@features/product/typings';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import { MIN_TOUCH_TARGET_SIZE } from '@utils/utils';

import { useConfigurator } from '../ConfiguratorContext';
import type { ConfiguratorProduct } from '../types';

type Props = {
  product: ConfiguratorProduct;
  discount: number;
};

export default function AlternativeCard({ product, discount }: Props) {
  const { trackEvent } = useAnalytics();
  const { isMobile } = useBreakpoints();
  const { t } = useTranslation('webshop.productPage.configurator');
  const { mainProduct } = useConfigurator();
  const { isInternalUser } = useRequestContext();

  if (!product || !product?.id) return null;

  const personalPrice = product.personalPrice?.price.includingVat || 0;
  const salesPrice = product.salesPrice?.includingVat || 0;
  const price = personalPrice || salesPrice || 0;

  const priceWithDiscount = price - price * (discount / 100);
  const hasDiscount = discount > 0;

  const onEnergyLabelContentClick = (
    content: 'product_information_sheet' | 'energy_label_image'
  ) => {
    trackEvent(
      selectEnergyLabelInformationTrigger({
        content
      })
    );
  };

  const usbCChargerIncluded =
    product.hasUsbCChargerIncluded !== null ? (
      <UsbCChargerIncluded hasUsbCChargerIncluded={product.hasUsbCChargerIncluded} />
    ) : undefined;

  const discountedPersonalPrice: PersonalPrice | undefined = personalPrice
    ? {
        price: {
          includingVat: priceWithDiscount
        },
        labelText: product.personalPrice?.labelText || ''
      }
    : undefined;

  const salesPriceComponent = (
    <SalesPrice
      currentPrice={<SalesPriceCurrent price={personalPrice ? salesPrice : priceWithDiscount} />}
      {...(hasDiscount && {
        formerPrice: <SalesPriceFormer price={salesPrice} />
      })}
      personalPrice={discountedPersonalPrice}
    />
  );

  if (isMobile)
    return (
      <StyledAlternativeCard
        attributes={
          product.highlights ? (
            <List gap='none'>
              {product.highlights.map((item, index) => (
                <ListItem key={`highlights-${index}`}>{item}</ListItem>
              ))}
            </List>
          ) : undefined
        }
        productId={product.id}
        key={product.name}
        productTitle={
          <ProductTitle>
            <ProductTitle.Title>
              <MiniProductPage
                productId={product.id}
                mainProductId={mainProduct.id}
                index={0}
                totalIndices={1}
                customSalesPrice={salesPriceComponent}
              >
                {product.name}
              </MiniProductPage>
            </ProductTitle.Title>

            {isInternalUser && product.urls?.product && (
              <Link href={product.urls.product} sx={{ minHeight: MIN_TOUCH_TARGET_SIZE }}>
                {t('internalProductLink')}
              </Link>
            )}

            <ProductTitle.Review
              size='small'
              value={product.reviewMetaData?.averageRating || 0}
              reviewCount={product.reviewMetaData?.count}
              showScore={false}
            />
          </ProductTitle>
        }
        orientation='horizontal'
        usbCChargerIncluded={usbCChargerIncluded}
        salesPrice={salesPriceComponent}
        promotionalProductImage={
          product.primaryImage?.id ? (
            <PromotionalProductImage
              alt={product.name || ''}
              imageId={product.primaryImage.id.toString()}
              size='small'
            />
          ) : (
            <></>
          )
        }
        energyLabel={
          product.energyLabel ? (
            <EnergyLabel
              level={product.energyLabel.level ?? 0}
              size='small'
              direction='right'
              imageId={Number(product.energyLabel.image?.id)}
              onContentClick={onEnergyLabelContentClick}
              {...(product?.energyLabel?.specification?.absoluteUrl && {
                specificationUrl: product.energyLabel.specification.absoluteUrl
              })}
            />
          ) : (
            <></>
          )
        }
      />
    );

  return (
    <StyledAlternativeCard
      ariaLabel={product.name}
      attributes={
        product.highlights ? (
          <List gap='none'>
            {product.highlights.map((item, index) => (
              <ListItem key={`highlights-${index}`}>{item}</ListItem>
            ))}
          </List>
        ) : undefined
      }
      productId={product.id}
      key={product.name}
      productTitle={
        <ProductTitle>
          <ProductTitle.Title>
            <MiniProductPage
              productId={product.id}
              mainProductId={mainProduct.id}
              index={0}
              totalIndices={1}
              customSalesPrice={salesPriceComponent}
            >
              {product.name}
            </MiniProductPage>
          </ProductTitle.Title>

          {isInternalUser && product.urls?.product && (
            <Link href={product.urls.product} sx={{ minHeight: MIN_TOUCH_TARGET_SIZE }}>
              {t('internalProductLink')}
            </Link>
          )}

          <ProductTitle.Review
            value={product.reviewMetaData?.averageRating || 0}
            reviewCount={product.reviewMetaData?.count}
            showScore={false}
          />
        </ProductTitle>
      }
      orientation='vertical'
      usbCChargerIncluded={usbCChargerIncluded}
      salesPrice={salesPriceComponent}
      promotionalProductImage={
        product.primaryImage?.id ? (
          <PromotionalProductImage
            alt={product.name || ''}
            imageId={product.primaryImage.id.toString()}
            size='medium'
          />
        ) : (
          <></>
        )
      }
      energyLabel={
        product.energyLabel ? (
          <EnergyLabel
            level={product.energyLabel.level ?? 0}
            size='small'
            direction='right'
            imageId={Number(product.energyLabel.image?.id)}
            onContentClick={onEnergyLabelContentClick}
            {...(product?.energyLabel?.specification?.absoluteUrl && {
              specificationUrl: product.energyLabel.specification.absoluteUrl
            })}
          />
        ) : (
          <></>
        )
      }
    />
  );
}

const StyledAlternativeCard = styled(ProductCard.Alternative)<AlternativeProductCardProps>(
  ({ theme }) => ({
    display: 'contents',

    '& > div': {
      '& p': {
        gridRow: '1'
      },

      gap: '0',
      gridTemplateRows: 'subgrid',
      gridColumn: 'span 12',
      gridRow: 'auto',

      [theme.breakpoint.minWidth('md')]: {
        gridColumn: 'span 4',
        gridRow: 'span 2'
      }
    }
  })
);
